import { useEffect, useRef, useState } from 'react'

/**
 * 月份區間選擇器：左右兩個年度面板，左邊挑起始月、右邊挑結束月。
 *
 * 值一律是 'YYYY-MM' 字串。因為格式固定，字串比大小就等於時間先後，
 * 後端也能直接用 period BETWEEN ? AND ? 查詢，不需要轉日期。
 */

const MONTHS = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月']

/**
 * 觸發鈕上的日曆圖示。
 * 原本只有純文字「2026-08」，看起來像唯讀欄位，不曉得可以點開換月份。
 */
function CalendarIcon() {
  return (
    <svg className="mrp-icon" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="1.5" y="3" width="13" height="11.5" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M1.5 6.5h13" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5 1.5v3M11 1.5v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

const ym = (year: number, month0: number) => `${year}-${String(month0 + 1).padStart(2, '0')}`

/** 點面板外面或按 Esc 就收起來 */
function useDismiss(open: boolean, close: () => void, box: React.RefObject<HTMLDivElement>) {
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) close()
    }
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open, close, box])
}

/**
 * 單月選擇器：跟區間版同一套面板樣式，但只有一個面板、選完就收。
 * 匯總表與請款明細是「以月結算」的文件，用這個而不是區間。
 */
export function MonthPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [year, setYear] = useState(Number(value.slice(0, 4)))
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => { if (open) setYear(Number(value.slice(0, 4))) }, [open, value])
  useDismiss(open, () => setOpen(false), box)

  return (
    <div className="mrp" ref={box}>
      <button type="button" className="mrp-trigger" style={{ minWidth: 110 }} onClick={() => setOpen(!open)}>
        <CalendarIcon />
        {value}
      </button>
      {open && (
        <div className="mrp-pop">
          <div className="mrp-panels">
            <div className="mrp-panel">
              <div className="mrp-head">
                <button type="button" onClick={() => setYear(year - 1)} aria-label="上一年">«</button>
                <span>{year}</span>
                <button type="button" onClick={() => setYear(year + 1)} aria-label="下一年">»</button>
              </div>
              <div className="mrp-grid">
                {MONTHS.map((m, i) => {
                  const v = ym(year, i)
                  return (
                    <button
                      type="button"
                      key={m}
                      className={v === value ? 'sel' : undefined}
                      aria-pressed={v === value}
                      onClick={() => { onChange(v); setOpen(false) }}
                    >
                      {m}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
          <div className="mrp-foot">
            <button type="button" onClick={() => { onChange(nowPeriod()); setOpen(false) }}>本月</button>
            <div className="spacer" />
            <button type="button" className="primary" onClick={() => setOpen(false)}>關閉</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function MonthRangePicker({
  from,
  to,
  onChange,
}: {
  from: string
  to: string
  onChange: (from: string, to: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [leftYear, setLeftYear] = useState(Number(from.slice(0, 4)))
  const [rightYear, setRightYear] = useState(Number(to.slice(0, 4)))
  const box = useRef<HTMLDivElement>(null)

  // 每次打開時把兩個面板對回目前選取的年份
  useEffect(() => {
    if (!open) return
    setLeftYear(Number(from.slice(0, 4)))
    setRightYear(Number(to.slice(0, 4)))
  }, [open, from, to])

  useDismiss(open, () => setOpen(false), box)

  /** 選起始月：若比結束月晚，就把結束月一起推過去，不讓區間顛倒 */
  const pickFrom = (v: string) => onChange(v, v > to ? v : to)
  /** 選結束月：若比起始月早，就把起始月一起拉過來 */
  const pickTo = (v: string) => onChange(v < from ? v : from, v)

  const Panel = ({
    year,
    setYear,
    selected,
    onPick,
    disabled,
    label,
  }: {
    year: number
    setYear: (y: number) => void
    selected: string
    onPick: (v: string) => void
    disabled: (v: string) => boolean
    label: string
  }) => (
    <div className="mrp-panel">
      <div className="mrp-head">
        <button type="button" onClick={() => setYear(year - 1)} aria-label="上一年">«</button>
        <span>{year}</span>
        <button type="button" onClick={() => setYear(year + 1)} aria-label="下一年">»</button>
      </div>
      <div className="mrp-grid" role="group" aria-label={label}>
        {MONTHS.map((m, i) => {
          const v = ym(year, i)
          const off = disabled(v)
          return (
            <button
              type="button"
              key={m}
              className={v === selected ? 'sel' : undefined}
              disabled={off}
              aria-pressed={v === selected}
              onClick={() => onPick(v)}
            >
              {m}
            </button>
          )
        })}
      </div>
    </div>
  )

  return (
    <div className="mrp" ref={box}>
      <button type="button" className="mrp-trigger" onClick={() => setOpen(!open)}>
        <CalendarIcon />
        {from === to ? from : `${from} ～ ${to}`}
      </button>

      {open && (
        <div className="mrp-pop">
          <div className="mrp-bar">
            <span>{from}</span>
            <em>～</em>
            <span>{to}</span>
          </div>
          <div className="mrp-panels">
            <Panel
              year={leftYear}
              setYear={setLeftYear}
              selected={from}
              onPick={pickFrom}
              disabled={() => false}
              label="起始月份"
            />
            <Panel
              year={rightYear}
              setYear={setRightYear}
              selected={to}
              // 結束月不能早於起始月，早的就變灰
              disabled={(v) => v < from}
              onPick={(v) => { pickTo(v); setOpen(false) }}
              label="結束月份"
            />
          </div>
          <div className="mrp-foot">
            <button type="button" onClick={() => { const n = nowPeriod(); onChange(n, n); setOpen(false) }}>
              本月
            </button>
            <button type="button" onClick={() => { const r = recentMonths(3); onChange(r[0], r[1]); setOpen(false) }}>
              近 3 個月
            </button>
            <button type="button" onClick={() => { const r = recentMonths(12); onChange(r[0], r[1]); setOpen(false) }}>
              近一年
            </button>
            <div className="spacer" />
            <button type="button" className="primary" onClick={() => setOpen(false)}>完成</button>
          </div>
        </div>
      )}
    </div>
  )
}

function nowPeriod(): string {
  const d = new Date(Date.now() + 8 * 3600 * 1000)
  return d.toISOString().slice(0, 7)
}

/** 回傳 [n 個月前, 本月]，含本月共 n 個月 */
function recentMonths(n: number): [string, string] {
  const d = new Date(Date.now() + 8 * 3600 * 1000)
  const end = ym(d.getFullYear(), d.getMonth())
  const s = new Date(d.getFullYear(), d.getMonth() - (n - 1), 1)
  return [ym(s.getFullYear(), s.getMonth()), end]
}
